import { useState } from "react";
import { CommentOutlined, FileAddOutlined, MenuOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Drawer, FloatButton, Timeline } from "antd";
import orderBy from "lodash.orderby";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { chatActions } from "@fitness-app/app-store";
import { type ClientNoteWithAuthorData } from "@fitness-app/data-models/entities/ClientNote";
import { TraineeStatus } from "@fitness-app/data-models/entities/Trainee";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { usePermissionScope } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useChatUtilitiesContext } from "~/modules/Chat/providers/ChatUtilitiesProvider";
import { useTimelineItem } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeNotes/useTimelineItem";
import { useTraineeProfile } from "~/modules/Trainee/TraineeProfile/scope";
import { supabase, useAppDispatch } from "~/store/initializeStore";

export const TraineeFloatingButton = () => {
  const { data: profile } = useTraineeProfile();
  const dispatch = useAppDispatch();
  const { memberId } = useUserClaims();
  // Do klientki spoza własnego przydziału pisze ten, kto ma czat szerzej niż
  // ASSIGNED — wcześniej wprost `isRegularTrainer`.
  const chatWriteScope = usePermissionScope(PermissionKey.CHAT_WRITE);
  const { isHeadCoach } = useUserRole();
  const { setNoteModel } = useChatUtilitiesContext();

  const [open, setOpen] = useState(false);
  const { createTimelineItem } = useTimelineItem();

  const { data } = useQuery(["floatingNotes", profile?.id], {
    keepPreviousData: true,
    queryFn: async () => {
      if (!profile) {
        return null;
      }
      const { data } = await supabase
        .from("client_note")
        .select("*, author:authorId(avatarUrl, firstName, lastName)")
        .order("createdAt", { ascending: false })
        .eq("traineeId", profile.id)
        .returns<ClientNoteWithAuthorData[]>();

      return data;
    },
  });

  if (!profile) {
    return null;
  }

  const clientNotAssigned = chatWriteScope === PermissionScope.ASSIGNED && profile.assignedTrainerId !== memberId;

  // Zarchiwizowana klientka NIE zamyka czatu — kanał zostaje do odczytu (pisanie
  // blokuje sam `ChatInput` po `channel.status`). Dawny warunek `ARCHIVED &&
  // isTeamMember` odcinał od niego cały personel poza właścicielem konta, więc
  // administracja z `chat.write` = Całe konto nie miała jak wejść w archiwum.
  const showMessageButton = !clientNotAssigned && profile.status !== TraineeStatus.INACTIVE && profile.userId;

  return (
    <>
      <FloatButton.Group trigger="hover" type="primary" className="floating-button" icon={<MenuOutlined />}>
        <FloatButton
          tooltip="Dodaj notatkę"
          icon={<FileAddOutlined />}
          onClick={() =>
            setNoteModel({
              traineeId: profile.id,
              content: "",
            })
          }
        />
        {data?.length ? (
          <FloatButton
            onClick={() => setOpen(true)}
            tooltip="Pokaż notatki"
            badge={{ count: data.length, color: "blue" }}
          />
        ) : (
          <></>
        )}
        {showMessageButton ? (
          <FloatButton
            onClick={() => {
              dispatch(
                chatActions.findAndAddChannelToList({
                  userId: profile.userId!,
                  isHeadCoach,
                }),
              );
            }}
            icon={<CommentOutlined />}
            tooltip="Wyślij wiadomość"
          />
        ) : (
          <></>
        )}
      </FloatButton.Group>
      <Drawer size="large" title="Notatki" onClose={() => setOpen(false)} open={open}>
        <Timeline mode="left">{orderBy(data, ["pinned"], ["desc"]).map(createTimelineItem)}</Timeline>
      </Drawer>
    </>
  );
};
